import { useState } from "react";
import { CaretDown, Toolbox } from "@phosphor-icons/react";

/**
 * AdminToolboxCard — collapsible container for low-frequency admin
 * tooling that used to live as 9 separate top-level cards on the admin
 * dashboard. Each child renders its own card UI; this just gates the
 * mount behind a click, which keeps the dashboard load-time fast and
 * the scroll length sane.
 *
 * Real user request May 2026: *"Create a truck help box (label it
 * something like that), inside that will be the Add a truck,
 * one-time announcement, audit precise pin, clean up cuisine tags,
 * schedule image extractor, test account hygiene, suspicious truck
 * name, pro access requests, ownership requests... You can have it
 * to see what each section is, you can either click on it or
 * something like that. Keep it clean and fast loading."*
 *
 * Implementation notes:
 *  - Each row is rendered ONLY when its accordion is open. This is
 *    not just a UX nicety — several of these cards (e.g. the schedule
 *    extractor, the email log) fire their own GET on mount. Lazy-
 *    mounting keeps the admin page initial load light.
 *  - The `summary` prop on each tool is the one-line "what does this
 *    do" hint. Keep it short — admins should be able to glance and pick.
 */
export default function AdminToolboxCard({ tools, defaultOpenKey = null }) {
  // We allow exactly one tool open at a time. Multiple-open would
  // chain re-fetches on render and defeat the lazy-mount goal.
  const [openKey, setOpenKey] = useState(defaultOpenKey);

  return (
    <section
      className="rounded-2xl border border-border bg-card overflow-hidden"
      data-testid="admin-toolbox-card"
    >
      <div className="px-4 py-3 border-b border-border bg-muted/20 flex items-center gap-2">
        <Toolbox weight="duotone" className="h-5 w-5 text-primary flex-none" />
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-semibold text-base leading-tight tracking-tight">
            Truck help box
          </h2>
          <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
            Less-used admin tools — click any row to load it on demand.
          </p>
        </div>
      </div>
      <ul className="divide-y divide-border">
        {tools.map((t) => {
          const isOpen = openKey === t.key;
          return (
            <li key={t.key} data-testid={`admin-toolbox-row-${t.key}`}>
              <button
                type="button"
                onClick={() => setOpenKey(isOpen ? null : t.key)}
                className={
                  "w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition " +
                  (isOpen ? "bg-muted/30" : "")
                }
                aria-expanded={isOpen}
                data-testid={`admin-toolbox-toggle-${t.key}`}
              >
                {t.icon && (
                  <t.icon
                    weight="duotone"
                    className="h-4 w-4 text-primary flex-none"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold leading-tight">{t.label}</div>
                  {t.summary && (
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                      {t.summary}
                    </div>
                  )}
                </div>
                <CaretDown
                  weight="bold"
                  className={
                    "h-3.5 w-3.5 text-muted-foreground transition-transform flex-none " +
                    (isOpen ? "rotate-180" : "")
                  }
                />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-1" data-testid={`admin-toolbox-body-${t.key}`}>
                  {/* Lazy-mounted — each tool component is only
                      instantiated when its row is open. */}
                  {typeof t.render === "function" ? t.render() : t.render}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
